/**
 * Pure edit logic for the `/config/:slug` staff-allowlist editor (2026-08-06).
 *
 * WHY THIS MODULE EXISTS — a real production incident. The editor keeps the row
 * list in React state; `update()` writes EVERY KEYSTROKE of the phone field into
 * it, while the admin checkbox instant-saves. Instant-saving sent the WHOLE
 * array, so ticking one row's admin box committed another row's half-typed
 * phone. The save path is a full-list `DELETE` + re-`INSERT` and writes no
 * `audit_log` row, so the damage was both immediate and untraceable.
 *
 * On 2026-08-06 that produced three corrupted variants of the operator's number
 * in one day (`…5279`, `…5271`, `…5275`). Each passed the server's E.164 regex —
 * they are well-formed numbers, just not anyone's — so validation could not
 * catch it, and every staff alert to that row silently bounced Meta 131026 until
 * someone noticed.
 *
 * The fix is to make the editor honour the contract its own comment already
 * claimed: "other fields still persist via Save allowlist". An admin toggle
 * saves the admin bit against the LAST SERVER-CONFIRMED list; in-progress text
 * stays local until the operator explicitly saves it.
 *
 * Pure + separately tested because `apps/dashboard/web/**` is ESLint-ignored and
 * excluded from coverage — the same reason `pr-person-edit.ts` and
 * `reply-channels.ts` exist.
 *
 * 2026-10-09 — ROWS HAVE AN IDENTITY, NOT A POSITION. The fix above addressed a
 * row in `saved` by its INDEX in the on-screen list, on the assumption that two
 * lists of equal length are in the same order. They are not: the Add button
 * appends a new row at the END, the server returns the list
 * `ORDER BY display_name, phone`, and a tick on a not-yet-saved row saves the
 * whole on-screen list. From that save on the two lists had the same length and
 * a different order, so every row sorting after the new name was off by one.
 * {@link mergeFlags} then painted each person with their neighbour's admin
 * bit, voter bit and notification grid, a tick landed on the neighbour, and the
 * next "Save allowlist" wrote the mis-painted screen to the table.
 *
 * On production that day a trainer was added and her first save was a tick. The
 * owner's row then showed un-ticked and the next person's showed ticked; two
 * corrective ticks removed the owner's admin and gave it to the newcomer, and
 * the Save that followed moved the owner's notification grid and voter grant
 * onto a trainer, that trainer's grid onto someone who had left, and left the
 * owner on a newcomer's defaults. The audit row for this action holds counts
 * only, and the counts never moved by more than one.
 *
 * So every row carries a `row_key` minted in the browser and never sent to the
 * server, and EVERYTHING here addresses a row by it: the two toggle builders,
 * the merge, the unsaved-edits check, the open Settings panel. Position is
 * never compared across lists again, and "the lists have diverged" means the
 * SET of keys differs, which also closes the remove-one-add-one case that an
 * equal length used to hide.
 */

export interface StaffAllowlistEntryDraft {
  /**
   * 2026-10-09 — this row's identity INSIDE THE EDITOR. Minted in the browser
   * ({@link mintRowKey}) when a row is loaded or added, carried across every
   * save by {@link adoptRowKeys}, and never sent to the server. Required, so a
   * construction site that forgets it is a compile error rather than a row
   * nothing can address. Opaque: do not derive it from the phone, which the
   * operator can retype.
   */
  row_key: string;
  phone: string;
  staff_id: string;
  display_name: string;
  /**
   * Optional to match the editor's own row type and the API response, where the
   * server may omit it. Absent is read as `false` everywhere below — never as
   * "unknown", because an unknown admin bit would be the one thing worth
   * refusing to save.
   */
  admin?: boolean;
  /**
   * Migration 160 — "Don't send me any messages". Orthogonal to `admin`: this
   * governs whether we CONTACT the person, `admin` governs what they may DO.
   * Same optional/absent-is-false convention as `admin`.
   */
  alerts_muted?: boolean;
  /**
   * 2026-08-12 — `notify_staff`'s email address and the inbound-Telegram
   * identity, now rendered by the editor. TEXT fields, so they are typed
   * character by character and belong in {@link TEXT_FIELDS} — not in
   * {@link FLAG_FIELDS}. Getting that wrong would reproduce this module's
   * founding bug in a new column: a flag toggle builds its POST from `saved`,
   * so an in-progress email edit would be silently discarded, and without the
   * field in `TEXT_FIELDS` the "you have unsaved edits" notice would not even
   * fire to say so.
   *
   * `null` = stored as unset. `undefined` = the row came from a client that did
   * not carry the field.
   */
  email?: string | null;
  telegram_chat_id?: string | null;
  /**
   * Migration 177 — the DASHBOARD LOGIN address, distinct from `email` (where
   * `notify_staff` writes). A TEXT field: it is typed character by character,
   * so it belongs in {@link TEXT_FIELDS} with the other two and NOT in
   * {@link FLAG_FIELDS}. Getting that wrong is this module's founding bug in
   * its most expensive column yet — a flag toggle builds its POST from
   * `saved`, so a half-typed login address would be silently discarded, and
   * the "you have unsaved edits" notice would not even fire to say so.
   *
   * `null` = stored as unset. `undefined` = the row came from a client that
   * did not carry the field.
   */
  login_email?: string | null;
  /**
   * Migration 173 — the voter grant (WHO may vote on staff decisions; the
   * grid's `decisions` row is WHERE the ballot reaches them). A flag like
   * `admin` / `alerts_muted`: instant-save checkbox, submitted-entry
   * semantics on the server's full replace, default FALSE.
   */
  voter?: boolean;
  /**
   * Migration 192 — the per-person notification grid
   * `{ "<type>": ["whatsapp"|"telegram"|"email", ...] }`, replacing the 17
   * `notify_*` boolean rails. The THIRD field class of this module:
   * instant-save like a flag (each tick in the three channel sections POSTs
   * at once), but STRUCTURED — so it has its own toggle builder
   * ({@link buildGridToggleList}) instead of riding {@link FLAG_FIELDS},
   * and {@link mergeFlags} folds it back from the server response like the
   * flags it sits beside. `undefined` = the row came from a client that did
   * not carry the field; the POST then OMITS the key, which the server
   * reads as "keep the stored grid" — the stale-tab guard. An absent TYPE
   * inside the grid is OFF, and empty arrays are never stored (a type with
   * no channels loses its key).
   */
  notification_grid?: Readonly<Record<string, ReadonlyArray<string>>>;
}

/** The text fields an operator edits by typing — the ones an instant-save must not touch. */
const TEXT_FIELDS = [
  "phone",
  "staff_id",
  "display_name",
  "email",
  "telegram_chat_id",
  "login_email",
] as const;

/**
 * The boolean fields that instant-save on click. Enumerated ONCE so a new flag
 * cannot be added to the row type and then silently dropped by a save: every
 * function below folds over this list rather than naming `admin` by hand.
 */
const FLAG_FIELDS = ["admin", "alerts_muted", "voter"] as const;

export type StaffAllowlistFlagField = (typeof FLAG_FIELDS)[number];

/**
 * What an ABSENT flag means — the server column's default. All three
 * remaining flags default FALSE (the 17 default-true `notify_*` rails left
 * with migration 192), but the lookup stays: the full-replace save writes an
 * omitted flag as its column default, so the editor must read absence the
 * same way or its normalised POST changes state.
 */
const FLAG_DEFAULTS: Record<StaffAllowlistFlagField, boolean> = {
  admin: false,
  alerts_muted: false,
  voter: false,
};

/** Every flag normalised to a real boolean — absent reads as its column default. */
function withNormalisedFlags(
  e: StaffAllowlistEntryDraft,
): StaffAllowlistEntryDraft {
  const out = { ...e };
  for (const f of FLAG_FIELDS) out[f] = e[f] ?? FLAG_DEFAULTS[f];
  return out;
}

let rowKeySeq = 0;

/**
 * A fresh row key, unique for the life of the page. A counter, not a UUID:
 * the key only has to tell this editor's rows apart, and a short readable one
 * makes a failing test legible.
 */
export function mintRowKey(): string {
  rowKeySeq += 1;
  return `row-${rowKeySeq}`;
}

/**
 * True when the two lists hold the SAME ROWS, in any order. This is the only
 * sense in which an on-screen list and a saved list can be compared: the
 * on-screen one keeps a new row at the end, the saved one is in the server's
 * sort order.
 */
function sameRows(
  a: ReadonlyArray<StaffAllowlistEntryDraft>,
  b: ReadonlyArray<StaffAllowlistEntryDraft>,
): boolean {
  if (a.length !== b.length) return false;
  const keys = new Set(a.map((e) => e.row_key));
  if (keys.size !== a.length) return false;
  return b.every((e) => keys.has(e.row_key));
}

/**
 * Give the rows a save RETURNED the identity of the rows that were SENT.
 *
 * The response is the whole list re-read in the server's sort order, so it
 * says nothing about which on-screen row each line is. The phone does: the
 * server stores it verbatim and refuses a list that repeats one
 * (`staff_allowlist_duplicate_phone`), so between what was posted and what
 * came back it is a one-to-one match. A returned row whose phone was not
 * posted (it should not happen) gets a fresh key, which makes the lists differ
 * as sets and so makes the server's list win wholesale in {@link mergeFlags}.
 */
export function adoptRowKeys<T extends { phone: string }>(
  serverRows: ReadonlyArray<T>,
  posted: ReadonlyArray<Pick<StaffAllowlistEntryDraft, "phone" | "row_key">>,
  mint: () => string = mintRowKey,
): Array<T & { row_key: string }> {
  const keyByPhone = new Map(posted.map((e) => [e.phone, e.row_key]));
  return serverRows.map((row) => ({
    ...row,
    row_key: keyByPhone.get(row.phone) ?? mint(),
  }));
}

/**
 * The list to POST for a flag-checkbox toggle (Admin, or "Don't send me any
 * messages").
 *
 * Built from `saved` (the last server-confirmed list), NOT from the in-progress
 * editor state — that is the entire fix. Only `field` on the row `rowKey`
 * differs; every OTHER flag on every row is carried across normalised, because
 * the save is a full-list replace and an omitted flag is erasure, not "leave
 * alone".
 *
 * Falls back to `local` when the two lists no longer hold the same rows (one
 * added or removed since the last save): `saved` then has no row to carry the
 * tick, or carries one the operator has removed. Better to persist what the
 * operator can actually see.
 *
 * The row is found by KEY in whichever list is the base. It was found by index
 * until 2026-10-09, which addressed the neighbour whenever the two lists were
 * in a different order (see this file's header).
 */
export function buildFlagToggleList(
  saved: ReadonlyArray<StaffAllowlistEntryDraft>,
  local: ReadonlyArray<StaffAllowlistEntryDraft>,
  rowKey: string,
  field: StaffAllowlistFlagField,
  checked: boolean,
): StaffAllowlistEntryDraft[] {
  const base = sameRows(saved, local) ? saved : local;
  return base.map((e) => {
    const normalised = withNormalisedFlags(e);
    return e.row_key === rowKey ? { ...normalised, [field]: checked } : normalised;
  });
}

/**
 * One grid cell flipped — the pure step both the POST builder below and the
 * editor's optimistic local reflect share, so the two can never disagree on
 * what a tick means. Adds/removes `channel` on `gridType`'s array and
 * DELETES the type's key when its last channel unticks: an empty array is
 * never produced, absence IS the off state.
 */
export function withGridCell(
  grid: Readonly<Record<string, ReadonlyArray<string>>> | undefined,
  gridType: string,
  channel: string,
  checked: boolean,
): Record<string, ReadonlyArray<string>> {
  const current = grid ?? {};
  const currentChannels = current[gridType] ?? [];
  const nextChannels = checked
    ? currentChannels.includes(channel)
      ? currentChannels
      : [...currentChannels, channel]
    : currentChannels.filter((c) => c !== channel);
  const next: Record<string, ReadonlyArray<string>> = { ...current };
  if (nextChannels.length === 0) {
    delete next[gridType];
  } else {
    next[gridType] = nextChannels;
  }
  return next;
}

/**
 * The list to POST for a notification-grid tick (one channel of one type on
 * one row, from the three channel sections).
 *
 * Same anti-keystroke-bug construction as {@link buildFlagToggleList}: built
 * from `saved`, only the target cell differs ({@link withGridCell}), the row
 * found by key, `local` fallback when the two lists hold different rows. Every other row's grid rides through
 * verbatim, and a row whose grid the client never carried keeps OMITTING the
 * key (server-side carry-over) rather than having one manufactured for it —
 * except the toggled row itself, whose single tick necessarily materialises
 * a grid to carry it.
 */
export function buildGridToggleList(
  saved: ReadonlyArray<StaffAllowlistEntryDraft>,
  local: ReadonlyArray<StaffAllowlistEntryDraft>,
  rowKey: string,
  gridType: string,
  channel: string,
  checked: boolean,
): StaffAllowlistEntryDraft[] {
  const base = sameRows(saved, local) ? saved : local;
  return base.map((e) => {
    const normalised = withNormalisedFlags(e);
    if (e.row_key !== rowKey) return normalised;
    return {
      ...normalised,
      notification_grid: withGridCell(
        e.notification_grid,
        gridType,
        channel,
        checked,
      ),
    };
  });
}

/**
 * Fold a server response back into the editor state after a flag or grid
 * save.
 *
 * Takes the server's flags AND notification grid (authoritative — that is
 * what was just saved) while KEEPING local text edits, which the server never
 * saw. A plain `setEntries(resp.entries)` would discard whatever the operator
 * was typing, which is how a half-typed value got committed in the first
 * place: the operator retyped it, and the retype raced the next save.
 *
 * Each on-screen row takes the values of the server row WITH ITS KEY
 * (`serverEntries` must already have been through {@link adoptRowKeys}), and
 * the on-screen order is kept so a row does not jump under the operator's
 * finger. Folding by position is what moved one person's admin bit, voter
 * grant and grid onto the next person on 2026-10-09.
 *
 * Different rows on the two sides ⇒ the server list wins wholesale; there is
 * no safe correspondence to preserve.
 */
export function mergeFlags(
  local: ReadonlyArray<StaffAllowlistEntryDraft>,
  serverEntries: ReadonlyArray<StaffAllowlistEntryDraft>,
): StaffAllowlistEntryDraft[] {
  if (!sameRows(local, serverEntries)) {
    return serverEntries.map(withNormalisedFlags);
  }
  const serverByKey = new Map(serverEntries.map((e) => [e.row_key, e]));
  return local.map((e) => {
    const server = serverByKey.get(e.row_key);
    const out = { ...e };
    for (const f of FLAG_FIELDS) out[f] = server?.[f] ?? FLAG_DEFAULTS[f];
    // The GET/save response always carries the grid (NOT NULL column); the
    // `?? {}` covers only a malformed response, and folding `{}` in beats
    // keeping a local guess the server may not hold.
    out.notification_grid = server?.notification_grid ?? {};
    return out;
  });
}

/**
 * True when any row's TEXT differs from the last server-confirmed list — i.e.
 * the operator has unsaved edits that a flag toggle deliberately did not
 * persist. Drives the notice that keeps the instant-save from being a silent
 * partial save. Flags are excluded: those save instantly by design.
 */
export function hasUnsavedTextEdits(
  local: ReadonlyArray<StaffAllowlistEntryDraft>,
  saved: ReadonlyArray<StaffAllowlistEntryDraft>,
): boolean {
  if (!sameRows(local, saved)) return true;
  const savedByKey = new Map(saved.map((e) => [e.row_key, e]));
  return local.some((entry) => {
    const before = savedByKey.get(entry.row_key);
    if (before === undefined) return true;
    // `?? null` because an absent key and a stored NULL are the same state and
    // must not read as an edit — otherwise the "unsaved edits" notice would be
    // permanently on for every row with no email, and a notice that is always
    // on is a notice nobody reads.
    return TEXT_FIELDS.some((f) => (entry[f] ?? null) !== (before[f] ?? null));
  });
}

/**
 * 2026-10-09 — the confirmation text for the staff list's Admin checkbox.
 *
 * WHY THIS EXISTS. Admin was one unconfirmed tick that saved the moment it was
 * touched. On 2026-10-09 it was changed twice in fifteen seconds against a
 * list whose rows had slipped by one (this file's header): the owner's admin
 * was removed, and a trainer added minutes earlier held it for twenty-five
 * minutes. On 2026-08-18 the list's admin count changed fourteen times in
 * about a minute and touched zero. No step asked, and nothing named the
 * person.
 *
 * Mario (2026-10-09): "when you tap, a second tap is also required, like a
 * confirmation before an admin is actually changed." So BOTH directions
 * confirm, and the text names the person the change will be WRITTEN to: the
 * caller passes the row out of the list it is about to post, not the row the
 * finger was on, so a prompt that names the wrong person is itself the alarm.
 *
 * Pure + exported so the wording is pinned by tests; `window.confirm` is the
 * dashboard's existing idiom for this (`channelAiConfirmMessage`).
 */
export function staffAdminConfirmMessage(args: {
  /** The name on the row the change is written to. */
  readonly displayName: string;
  /** That row's phone, for a row that has no name yet. */
  readonly phone: string;
  /** True = making them an admin; false = removing it. */
  readonly makeAdmin: boolean;
  /** How many admins the list holds once this change is saved. */
  readonly adminsAfter: number;
}): string {
  const name = args.displayName.trim();
  const phone = args.phone.trim();
  const who = name.length > 0 ? name : phone.length > 0 ? phone : "this person";

  if (args.makeAdmin) {
    return [
      `Make ${who} an admin?`,
      "",
      `${who} will be able to run every staff action through the assistant, including the destructive ones, and will receive the admin-only digests and alerts. Nothing changes for anyone else.`,
    ].join("\n");
  }

  const nobodyLeft =
    args.adminsAfter === 0
      ? " After this, NOBODY on this list is an admin."
      : "";
  return [
    `Remove admin from ${who}?`,
    "",
    `${who} goes back to normal staff: the assistant will run only the actions on the staff list for them, and the admin-only digests and alerts stop reaching them.${nobodyLeft} Nothing changes for anyone else.`,
  ].join("\n");
}
